/**
 * Utilitaires pour le free trial sans CB
 *
 * Le free trial est stocké dans user_metadata :
 * - free_trial_started: string (ISO date)
 * - free_trial_plan: 'pro'
 *
 * Durée : 14 jours à partir de free_trial_started
 */

import type { User } from '@supabase/supabase-js';

// Durée du free trial en jours
export const FREE_TRIAL_DURATION_DAYS = 14;

/**
 * Vérifie si l'utilisateur est en période de free trial (sans CB)
 * Retourne false si l'utilisateur n'a jamais démarré de trial
 */
export function isInFreeTrial(user: User | null): boolean {
  if (!user) return false;

  const trialStarted = user.user_metadata?.free_trial_started;
  if (!trialStarted) return false;

  const startDate = new Date(trialStarted);
  if (isNaN(startDate.getTime())) return false;

  const now = new Date();
  const diffMs = now.getTime() - startDate.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  return diffDays < FREE_TRIAL_DURATION_DAYS;
}

/**
 * Retourne le nombre de jours restants dans le free trial
 * Retourne 0 si le trial est expiré ou n'a jamais été démarré
 */
export function getFreeTrialDaysLeft(user: User | null): number {
  if (!user) return 0;

  const trialStarted = user.user_metadata?.free_trial_started;
  if (!trialStarted) return 0;

  const startDate = new Date(trialStarted);
  if (isNaN(startDate.getTime())) return 0;

  const now = new Date();
  const diffMs = now.getTime() - startDate.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  const remaining = FREE_TRIAL_DURATION_DAYS - diffDays;

  return Math.max(0, Math.ceil(remaining));
}

/**
 * Vérifie si l'utilisateur a déjà eu un free trial (même expiré)
 */
export function hasHadFreeTrial(user: User | null): boolean {
  if (!user) return false;
  return !!user.user_metadata?.free_trial_started;
}
